import { Component,
  OnInit,
  ViewChild,
  ElementRef,
  Input,
  Renderer2,
  OnDestroy,
  AfterViewInit,
  ChangeDetectionStrategy,
  ViewEncapsulation, 
  ChangeDetectorRef} from '@angular/core';
import { Subject, fromEvent, BehaviorSubject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { trigger, transition, sequence, style, animate, state } from '@angular/animations';

@Component({
  selector: 'app-view-progress',
  templateUrl: './view-progress.component.html',
  styleUrls: ['./view-progress.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('isOpen', [
      state('open', style({
        transform: 'scale(1)'
      })),
      state('closed', style({
        transform: 'scale(0)'
      })),
      transition('closed => open', [
        animate('300ms ease-out')
      ]),
      transition('open => closed', [
        animate('150ms ease-in')
      ])
    ])
  ]
})
export class ViewProgressComponent implements OnInit, OnDestroy, AfterViewInit {

  @ViewChild('circle', { static: true })
  circleRef: ElementRef<HTMLElement>;

  @ViewChild('progressCircle', { static: true })
  progressCircleRef: ElementRef<SVGCircleElement>;

  @ViewChild('progressContainer', { static: true })
  progressContainerRef: ElementRef<SVGCircleElement>;

  ngUnsubscribe = new Subject();
  label = 'Loading...';


  // ---------- Inputs ----------
  @Input()
  public get show() {
    return this._show;
  }
  public set show(value) {
    setTimeout(() => {
      this._show = value;
      this.changeDetectorRef.markForCheck();
    });
  }
  private _show = false;

  @Input()
  public get percent() {
    return Math.floor(this._percent.value);
  }
  public set percent(value) {
    if (value == null) {
      return;
    }

    this._percent.next(value);
  }
  private _percent = new BehaviorSubject<number>(0);



  // ---------- Lifecycle ----------
  constructor(
    private renderer: Renderer2,
    private changeDetectorRef: ChangeDetectorRef
  ) { }

  ngOnInit() {
    this.initSizeObserver();
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  ngAfterViewInit() {
    this.updateDimensions();
    this.initPercentObserver();
  }

  // ---------- Class Methods ----------
  initPercentObserver() {
    this._percent.pipe(
      takeUntil(this.ngUnsubscribe)
    ).subscribe(() => {
      this.label = this.getLoadingLabel(this.percent);
      
      // When percent is zero, render a full circle. Otherwise render the percentage. 
      this.renderProgress(this.percent === 0 ? 100 : this.percent);
      
      this.changeDetectorRef.markForCheck();
    });
  }

  initSizeObserver() {
    // Listen to view page resize
    const windowResize$ = fromEvent(window, 'resize');
    windowResize$
      .pipe(
        debounceTime(10),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(() => {
        this.updateDimensions();
      });
  }

  calculateRadius() {
    return this.getCircleWidth() / 2;
  }

  getCircleWidth() {
    return this.circleRef.nativeElement.offsetWidth;
  }

  getRadius() {
    return this.progressCircleRef.nativeElement.r.baseVal.value;
  }

  getCircumference() {
    const radius = this.getRadius();
    return radius * 2 * Math.PI;
  }

  getOffset(percent = 100) {
    const circumference = this.getCircumference();
    return circumference - (percent / 100 * circumference);
  }

  renderProgress(percent: number) {
    if (percent >= 100) {
      percent = 100;
    }

    const circumference = this.getCircumference();
    const offset = this.getOffset(percent);

    this.renderer.setStyle(this.progressCircleRef.nativeElement, 'strokeDasharray', `${circumference} ${circumference}`);
    this.renderer.setStyle(this.progressCircleRef.nativeElement, 'strokeDashoffset', `${offset}`);
  }

  getLoadingLabel(percent: number): string {
    if (percent === 100) {
      return 'Launching...';
    }

    if (percent > 0) {
      return 'Loading...';
    }

    return 'Loading...';
  }

  updateDimensions() {
    const radius = this.calculateRadius();

    this.renderer.setAttribute(this.progressContainerRef.nativeElement, 'width', `${radius * 2}`);
    this.renderer.setAttribute(this.progressContainerRef.nativeElement, 'height', `${radius * 2}`);

    this.renderer.setAttribute(this.progressCircleRef.nativeElement, 'r', `${radius - 6}`);
    this.renderer.setAttribute(this.progressCircleRef.nativeElement, 'cx', `${radius}`);
    this.renderer.setAttribute(this.progressCircleRef.nativeElement, 'cy', `${radius}`);

    const circumference = this.getCircumference();

    this.renderer.setStyle(this.progressCircleRef.nativeElement, 'strokeDasharray', `${circumference} ${circumference}`);
    this.renderer.setStyle(this.progressCircleRef.nativeElement, 'strokeDashoffset', `${circumference}`);
  }


}
